import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

export interface LowStockItem {
  id: string;
  itemCode: string;
  itemName: string;
  outlet: string;
  outletName: string;
  currentQty: number;
  minimumQty: number;
  unit: string;
  category: string;
  percentage: number; // Current qty as percentage of minimum
  status: 'critical' | 'low' | 'warning';
}

interface LowStockResponse {
  data: LowStockItem[];
  total: number;
}

export function useLowStockAlerts(limit: number = 10) {
  return useQuery({
    queryKey: ['dashboard', 'low-stock', limit],
    queryFn: async () => {
      const response = await apiClient.get<LowStockResponse>('/inventory/low-stock', {
        limit,
        sortBy: 'percentage',
        sortOrder: 'asc',
      });
      return response;
    },
    refetchInterval: 300000, // Refresh every 5 minutes
    staleTime: 120000, // Consider data stale after 2 minutes
    gcTime: 600000, // Keep in cache for 10 minutes
    retry: 2,
  });
}

export interface OutOfStockItem {
  id: string;
  itemCode: string;
  itemName: string;
  outlet: string;
  outletName: string;
  unit: string;
  category: string;
  lastMovementDate: string | null;
}

interface OutOfStockResponse {
  data: OutOfStockItem[];
  total: number;
}

export function useOutOfStockAlerts(limit: number = 10) {
  return useQuery({
    queryKey: ['dashboard', 'out-of-stock', limit],
    queryFn: async () => {
      const response = await apiClient.get<OutOfStockResponse>('/inventory/out-of-stock', {
        limit,
      });
      return response;
    },
    refetchInterval: 300000,
    staleTime: 120000,
    gcTime: 600000,
  });
}

export interface ExpiringItem {
  id: string;
  itemCode: string;
  itemName: string;
  outlet: string;
  outletName: string;
  batchNo: string;
  quantity: number;
  expiryDate: string;
  daysUntilExpiry: number;
  status: 'expired' | 'critical' | 'warning';
}

interface ExpiringItemsResponse {
  data: ExpiringItem[];
  total: number;
}

export function useExpiringItems(daysAhead: number = 30, limit: number = 10) {
  return useQuery({
    queryKey: ['dashboard', 'expiring-items', daysAhead, limit],
    queryFn: async () => {
      const response = await apiClient.get<ExpiringItemsResponse>('/inventory/expiring', {
        daysAhead,
        limit,
      });
      return response;
    },
    refetchInterval: 3600000, // Refresh every hour
    staleTime: 600000, // Consider data stale after 10 minutes
  });
}
